// frontend/src/App.js
import "./App.css";
import React, { useEffect, useState, useCallback } from "react";
import {
  NavLink,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import ChangePW from "./components/ChangePW";
import CustomerForm from "./components/CustomerForm";
import CustomerList from "./components/CustomerList";
import ProductManager from "./components/ProductManager";
import QuoteBuilder from "./components/QuoteBuilder";
import QuotesPage from "./components/QuotesPage";
import CalendarPage from "./pages/CalendarPage";
import InvoicesPage from "./pages/InvoicesPage";
import AdminPage from "./pages/AdminPage";
import SettingsPage from "./components/AdminSettings";
import EmailSettingsPage from "./pages/EmailSettingsPage";
import AdminIOSCalendarLink from "./pages/AdminIOSCalendarLink";
import AdminInvoicesEditor from "./pages/AdminInvoicesEditor";


/* ===== Service Reports ===== */
import SRAdminEngineers from "./pages/SR_AdminEngineers";
import SRAdminTasks from "./pages/SR_AdminTasks";
import SRAdminReports from "./pages/SR_AdminReports";
import SREngineerChecklist from "./pages/SR_EngineerChecklist";
/* NEW: Templates page */
import SRAdminTemplates from "./pages/SR_AdminTemplates";
/* =========================== */

/* SR API for template list */
import { sr } from "./api/serviceReports";

/* === NEW: Email Messages page === */
import EmailMessages from "./pages/EmailMessages";

/* ---------- Auth helpers ---------- */
function isAuthed() {
  return !!(window.appAuth?.token || sessionStorage.getItem("accessToken"));
}
function Private({ children }) {
  const loc = useLocation();
  return isAuthed() ? children : <Navigate to="/login" replace state={{ from: loc.pathname }} />;
}
/* ---------------------------------- */

/* Inline "Create Service Report" button + template picker (shown on /admin) */
function CreateReportInline() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const rows = await sr.templates.list(); // active by default
      setTemplates(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) load();
  }, [open]);

  function goManual() {
    setOpen(false);
    navigate("/service-reports/engineer"); // no template param
  }
  function choose(tplId) {
    setOpen(false);
    navigate(`/service-reports/engineer?template=${tplId}`);
  }

  return (
    <>
      {/* Inline buttons on Admin page */}
      <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: "wrap" }}>
        <button className="btn" onClick={() => setOpen(true)}>
          + Create Service Report
        </button>
        <button
          className="btn secondary"
          onClick={() => navigate("/service-reports/admin/templates")}
        >
          Manage Report Templates
        </button>
        {/* NEW: Email messages button (kept in Admin area, not the main menu) */}
        <button
          className="btn secondary"
          onClick={() => navigate("/email-messages")}
          title="Edit default email bodies for Quotes & Invoices"
        >
          Email messages
        </button>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div className="card" style={{ width: "min(560px, 92vw)", maxHeight: "80vh", overflow: "auto", padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Start a Service Report</h3>
            <p style={{ marginTop: 4, color: "#555" }}>
              Pick a report template to pre-load only the relevant tasks. You can still add notes and capture a signature.
            </p>

            <div style={{ marginTop: 10, marginBottom: 12 }}>
              <button className="btn secondary" onClick={goManual}>
                Manual (no template)
              </button>
            </div>

            {loading ? (
              <div>Loading templates…</div>
            ) : err ? (
              <div style={{ color: "#b91c1c" }}>Error: {err}</div>
            ) : templates.length === 0 ? (
              <div style={{ color: "#666" }}>No active templates yet.</div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {templates.map((t) => (
                  <button
                    key={t.id}
                    className="btn"
                    style={{ justifyContent: "space-between", display: "flex" }}
                    onClick={() => choose(t.id)}
                  >
                    <span>{t.name}</span>
                    <span style={{ opacity: 0.8, fontWeight: 400 }}>
                      {t.description || "Template"}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button className="btn secondary" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* Wrapper to render Admin screen + inline create button */
function AdminWithCreateReport() {
  return (
    <>
      <AdminPage />
      <CreateReportInline />
    </>
  );
}

function App() {
  const navigate = useNavigate();
  const loc = useLocation();

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Try refresh on load to keep signed in
  useEffect(() => {
    (async () => {
      if (sessionStorage.getItem("accessToken")) return;
      try {
        const r = await fetch("/api/auth/refresh", { method: "POST", credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          sessionStorage.setItem("accessToken", d.accessToken);
        }
      } catch {}
    })();
  }, []);

  const handleLogout = useCallback(async () => {
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "include" }); } catch {}
    sessionStorage.removeItem("accessToken");
    setShowLogoutConfirm(false);
    setMobileOpen(false);
    navigate("/login", { replace: true });
  }, [navigate]);

  // ✅ Close drawer only when the route actually changes
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  // ✅ Lock body scroll while drawer open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = mobileOpen ? "hidden" : prev || "";
    return () => { document.body.style.overflow = prev || ""; };
  }, [mobileOpen]);

  // Escape closes dialogs/drawer
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showLogoutConfirm) setShowLogoutConfirm(false);
      else if (mobileOpen) setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showLogoutConfirm, mobileOpen]);

  // Focus trap for drawer
  useEffect(() => {
    if (!mobileOpen) return;
    const drawer = document.getElementById("mobile-menu");
    if (!drawer) return;
    const selector = 'a, button, [tabindex]:not([tabindex="-1"])';
    const focusables = drawer.querySelectorAll(selector);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      if (focusables.length === 0) return;
      if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    };
    first && first.focus();
    drawer.addEventListener("keydown", onKeyDown);
    return () => drawer.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  // Helper to close drawer when clicking a mobile link
  const closeMobileAnd = (fn) => (e) => { if (fn) fn(e); setMobileOpen(false); };

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-inner" style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* Left: brand */}
          <div className="brand" style={{ display: "flex", alignItems: "center", textAlign: "center", flex: "0 0 auto" }}>
            <img
              className="brand-logo"
              src="/uploads/logo/logo.png"
              alt="JW Clima Solutions"
              style={{ height: 70, width: "auto" }}
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
            <div style={{ color: "#fff", fontWeight: 800, lineHeight: 1.15, marginTop: 6 }}>
              JW Clima Solutions
            </div>
          </div>

          {/* Desktop nav */}
          <nav
            className="nav nav-desktop"
            style={{ marginLeft: "auto", display: "flex", flex: 1, maxWidth: 980, justifyContent: "space-evenly", gap: 8, flexWrap: "wrap", alignItems: "center" }}
          >
            <NavLink to="/" end>Add Customer</NavLink>
            <NavLink to="/customers">Edit Customer</NavLink>
            <NavLink to="/products">Product Manager</NavLink>
            <NavLink to="/quote">Quote Builder</NavLink>
            <NavLink to="/quotes">Look up Quote</NavLink>
            <NavLink to="/calendar">Calendar</NavLink>
            <NavLink to="/invoices">Look up Invoices</NavLink>

            {/* NEW: place before Service Reports */}
            <NavLink to="/service-reports/engineer">New Service Report</NavLink>
            <NavLink to="/service-reports/reports">Service Reports</NavLink>

            <NavLink to="/admin">Admin</NavLink>

            {isAuthed() && (
              <button className="btn secondary" onClick={() => setShowLogoutConfirm(true)}>
                Logout
              </button>
            )}
          </nav>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="menu-btn"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-controls="mobile-menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
              {mobileOpen ? (
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              ) : (
                <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile drawer + scrim */}
        <div
          className={`scrim ${mobileOpen ? "show" : ""}`}
          hidden={!mobileOpen}
          onClick={() => setMobileOpen(false)}
        />
        <aside
          id="mobile-menu"
          className={`drawer ${mobileOpen ? "open" : ""}`}
          hidden={!mobileOpen}
          aria-label="Mobile menu"
          role="dialog"
          aria-modal="true"
        >
          <nav className="nav-mobile" onClick={(e) => e.stopPropagation()}>
            <NavLink to="/" end onClick={closeMobileAnd()} >Add Customer</NavLink>
            <NavLink to="/customers" onClick={closeMobileAnd()} >Edit Customer</NavLink>
            <NavLink to="/products" onClick={closeMobileAnd()} >Product Manager</NavLink>
            <NavLink to="/quote" onClick={closeMobileAnd()} >Quote Builder</NavLink>
            <NavLink to="/quotes" onClick={closeMobileAnd()} >Look up Quote</NavLink>
            <NavLink to="/calendar" onClick={closeMobileAnd()} >Calendar</NavLink>
            <NavLink to="/invoices" onClick={closeMobileAnd()} >Look up Invoices</NavLink>

            {/* NEW: place before Service Reports */}
            <NavLink to="/service-reports/engineer" onClick={closeMobileAnd()} >New Service Report</NavLink>
            <NavLink to="/service-reports/reports" onClick={closeMobileAnd()} >Service Reports</NavLink>

            <NavLink to="/admin" onClick={closeMobileAnd()} >Admin</NavLink>

            {isAuthed() && (
              <button
                className="btn secondary"
                onClick={() => { setMobileOpen(false); setShowLogoutConfirm(true); }}
              >
                Logout
              </button>
            )}
          </nav>
        </aside>
      </header>

      <main className="container">
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />

          {/* Private */}
          <Route path="/" element={<Private><CustomerForm /></Private>} />
          <Route path="/customers" element={<Private><CustomerList /></Private>} />
          <Route path="/products" element={<Private><ProductManager /></Private>} />
          <Route path="/quote" element={<Private><QuoteBuilder /></Private>} />
          <Route path="/quote-builder" element={<Private><QuoteBuilder /></Private>} />
          <Route path="/quotes" element={<Private><QuotesPage /></Private>} />
          <Route path="/calendar" element={<Private><CalendarPage /></Private>} />
          <Route path="/invoices" element={<Private><InvoicesPage /></Private>} />

          {/* Admin wrapped WITH inline Create Report button */}
          <Route path="/admin" element={<Private><AdminWithCreateReport /></Private>} />

          <Route path="/settings" element={<Private><SettingsPage /></Private>} />
          <Route path="/admin/email" element={<Private><EmailSettingsPage /></Private>} />
          <Route path="/admin/change-password" element={<Private><ChangePW /></Private>} />
          <Route path="/admin/ios-calendar" element={<Private><AdminIOSCalendarLink /></Private>} />
          <Route path="/admin/invoices" element={<Private><AdminInvoicesEditor /></Private>} />


          {/* ===== Service Reports routes ===== */}
          <Route path="/service-reports/engineer" element={<Private><SREngineerChecklist /></Private>} />
          <Route path="/service-reports/admin/engineers" element={<Private><SRAdminEngineers /></Private>} />
          <Route path="/service-reports/admin/tasks" element={<Private><SRAdminTasks /></Private>} />
          {/* Keep the old admin path working */}
          <Route path="/service-reports/admin/reports" element={<Private><SRAdminReports /></Private>} />
          {/* Public (auth-gated) route for Reports, linked in nav */}
          <Route path="/service-reports/reports" element={<Private><SRAdminReports /></Private>} />
          {/* Templates manager route */}
          <Route path="/service-reports/admin/templates" element={<Private><SRAdminTemplates /></Private>} />
          {/* ================================= */}

          {/* NEW: Email messages editor */}
          <Route path="/email-messages" element={<Private><EmailMessages /></Private>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Logout confirmation modal */}
      {showLogoutConfirm && (
        <div
          className="qp-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-title"
          onClick={(e) => { if (e.target === e.currentTarget) setShowLogoutConfirm(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div className="qp-card" style={{ minWidth: 320, maxWidth: "90vw", padding: 16 }}>
            <h3 id="logout-title" style={{ margin: 0 }}>Log out?</h3>
            <div style={{ marginTop: 8 }}>Are you sure you want to log out?</div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button className="btn secondary" onClick={() => setShowLogoutConfirm(false)}>Cancel</button>
              <button className="btn" onClick={handleLogout}>Yes, log out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
